import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import axios from "axios";
import { BASE_URL } from "../../App";
import React, { useState } from "react";
import Swal from "sweetalert2";

const DealerAndSubDealerReport = () => {
    const [uploadedData, setUploadedData] = useState([]);
    const [loadingDownload, setLoadingDownload] = useState(false);
    const [loadingUpload, setLoadingUpload] = useState(false);

    const USER_ID = localStorage.getItem('USER_ID');

    // ✅ Download Excel
    const handleDownload = async () => {
        setLoadingDownload(true);
        try {
            const response = await axios.get(BASE_URL + '/getdealerandsubdealer');
            const jsonData = response.data.data;

            const worksheet = XLSX.utils.json_to_sheet(jsonData);
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, "Dealers");

            const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
            const blob = new Blob([excelBuffer], { type: "application/octet-stream" });
            saveAs(blob, "DealerSubDealerReport.xlsx");
        } catch (error) {
            console.error("Download failed:", error);
            alert("Download failed!");
        } finally {
            setLoadingDownload(false);
        }
    };

    // ✅ Upload Excel
    const handleUpload = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        setLoadingUpload(true);

        const reader = new FileReader();
        reader.onload = async (e) => {
            try {
                const data = new Uint8Array(e.target.result);
                const workbook = XLSX.read(data, { type: "array" });
                const worksheetName = workbook.SheetNames[0];
                const worksheet = workbook.Sheets[worksheetName];
                const jsonData = XLSX.utils.sheet_to_json(worksheet);

                
                await axios.post(BASE_URL + "/uploaddealerandsubdealer", { data: jsonData, username: USER_ID })
                    .then(response => {
                        console.log("API response:", response.data);

                        // ✅ Safely handle response
                        const apiData = response.data?.data || [];
                        setUploadedData(apiData);

                        let msg = response.data.message || "Upload completed!";
                        msg += `\nUpdated Rows: ${response.data.updated_rows ?? 0}`;

                        // ✅ Handle row-wise errors
                        const errors = response.data.errors || {};
                        if (Object.keys(errors).length > 0) {
                            msg += `\n\n⚠️ Validation Errors:\n`;
                            for (let row in errors) {
                                msg += `Row ${row}:\n`;
                                for (let field in errors[row]) {
                                    msg += ` - ${field}: ${errors[row][field].join(", ")}\n`;
                                }
                            }
                        }

                        Swal.fire({
                            icon: Object.keys(errors).length > 0 ? 'warning' : 'success',
                            title: Object.keys(errors).length > 0 ? 'Upload Completed with Errors' : 'Success',
                            text: msg,
                            customClass: { popup: 'swal-wide' },
                        });
                    })
                    .catch(error => {
                        console.error("API error:", error);
                        Swal.fire({
                            icon: 'error',
                            title: 'Error',
                            text: 'Upload failed!',
                        });
                    });

            } catch (err) {
                console.error("API upload failed:", err);
                //alert("Upload failed!");
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: 'Upload failed!',
                });
            } finally {
                setLoadingUpload(false);
                event.target.value = "";
            }
        };
        reader.readAsArrayBuffer(file);
    };

    return (
        <div style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100vh',
            gap: '20px',
            background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)'
        }}>
            {/* Download Button */}
            <button
                onClick={handleDownload}
                disabled={loadingDownload}
                style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    padding: "12px 32px",
                    fontSize: "1.1rem",
                    fontWeight: "bold",
                    color: "#fff",
                    background: loadingDownload
                        ? "gray"
                        : "linear-gradient(90deg, #0072ff 0%, #00c6ff 100%)",
                    border: "none",
                    borderRadius: "8px",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                    cursor: loadingDownload ? "not-allowed" : "pointer",
                    transition: "all 0.3s ease"
                }}
            >
                {loadingDownload ? (
                    <span>Downloading...</span>
                ) : (
                    <>
                        <svg width="20" height="20" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                            <path d="M12 5v14M19 12l-7 7-7-7"/>
                        </svg>
                        Download Excel
                    </>
                )}
            </button>

            {/* Upload Button */}
            <label
                style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    padding: "12px 32px",
                    fontSize: "1.1rem",
                    fontWeight: "bold",
                    color: "#fff",
                    background: loadingUpload
                        ? "gray"
                        : "linear-gradient(90deg, #ff758c 0%, #ff7eb3 100%)",
                    borderRadius: "8px",
                    cursor: loadingUpload ? "not-allowed" : "pointer",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                    transition: "all 0.3s ease"
                }}
            >
                {loadingUpload ? (
                    <span>Uploading...</span>
                ) : (
                    <>
                        <svg width="20" height="20" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                            <path d="M12 19V5M5 12l7-7 7 7"/>
                        </svg>
                        Upload Excel
                    </>
                )}
                <input
                    type="file"
                    accept=".xlsx, .xls"
                    onChange={handleUpload}
                    style={{ display: "none" }}
                    disabled={loadingUpload}
                />
            </label>

            {/* Preview uploaded data */}
            {uploadedData && uploadedData.length > 0 && (
                <div style={{ maxHeight: "300px", overflow: "auto", marginTop: "20px", width: "90%" }}>
                    <table border="1" style={{ borderCollapse: "collapse", width: "100%" }}>
                        <thead>
                            <tr style={{ background: "#006c07ff" }}>
                                {Object.keys(uploadedData[0]).map((key, index) => (
                                    <th key={index} style={{ padding: "8px", background: "#006c07ff", color: '#eee' }}>{key}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {uploadedData.map((row, rowIndex) => (
                                <tr key={rowIndex}>
                                    {Object.values(row).map((val, colIndex) => (
                                        <td key={colIndex} style={{ padding: "6px" }}>{val}</td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

        </div>
    );
};

export default DealerAndSubDealerReport;
